import React from 'react';
import { SpinningLoadingIcon } from '~/libs/ui/atoms/SpinningLoadingIcon';
import { PaginationPanel } from '~/libs/ui/molecules/PaginationPanel';

import { availabilityGridColumns } from '../constants';
import { AvailabilityListComponentProps } from '../types/AvailabilityListProps';
import { countGroupGridRows } from '../utils/countGroupGridRows';
import { PerPageSelect } from './PerPageSelect';
import { Row } from './Row';
import { TableHeader } from './TableHeader';

// Repeating bars, so the placeholder fills whatever height the outgoing rows occupied without measuring them.
const SKELETON_ROWS_STYLE: React.CSSProperties = {
	backgroundImage: 'linear-gradient(to bottom, #e5e7eb 0, #e5e7eb 44px, transparent 44px, transparent 56px)',
	backgroundSize: '100% 56px',
	backgroundRepeat: 'repeat-y',
};

export function AvailabilityList({
	data,
	isFetching,
	isError,
	dddShareThreshold,
	selectable,
	selectedIds,
	selectionDisabled,
	onToggleSelect,
	perPage,
	onPageChange,
	onPerPageChange,
	expandedKeys,
	onToggleExpand,
}: AvailabilityListComponentProps) {
	if (isError) {
		return <div className='py-10 text-center text-red-600'>Data se nepodařilo načíst. Zkuste to prosím znovu.</div>;
	}

	// With no rows to hold the layout there is nothing to overlay, so this state carries the spinner itself.
	if (isFetching && !data?.items.length) {
		return (
			<div className='flex items-center justify-center gap-2 py-10 text-sm font-semibold text-gray-600'>
				<SpinningLoadingIcon className='text-cPurple-600' />
				Načítání…
			</div>
		);
	}

	if (!data) {
		return null;
	}

	if (data.items.length === 0) {
		return <div className='py-10 text-center text-gray-500'>Nic nenalezeno.</div>;
	}

	const columns = availabilityGridColumns(selectable);
	let nextRow = 1;

	return (
		<div className={isFetching ? 'pointer-events-none' : undefined}>
			<TableHeader columns={columns} />

			<div className='relative'>
				<div className='xl:grid xl:pt-3 xl:gap-y-0' style={{ gridTemplateColumns: columns }}>
					{data.items.map((group, index) => {
						const startRow = nextRow;
						nextRow += countGroupGridRows(group);

						return (
							<Row
								key={group.id}
								group={group}
								dddShareThreshold={dddShareThreshold}
								layout='grid'
								startRow={startRow}
								showGroupSeparator={index > 0}
								selectable={selectable}
								selected={selectedIds.has(group.id)}
								selectionDisabled={selectionDisabled}
								onToggleSelect={onToggleSelect}
								expandedKeys={expandedKeys}
								onToggleExpand={onToggleExpand}
							/>
						);
					})}
				</div>

				{/* Out of flow and sized by the rows underneath, so the placeholder never reflows the page.
				    The spinner sticks to the viewport to stay visible while the page scrolls back up. */}
				{isFetching && (
					<div className='absolute left-0 top-0 w-full h-full bg-gray-100'>
						<div className='absolute inset-0 animate-pulse' style={SKELETON_ROWS_STYLE} />

						<div className='relative h-full flex justify-center'>
							<div className='sticky top-[40vh] flex items-center gap-2 h-9 px-4 rounded-full bg-white shadow-md text-sm font-semibold text-gray-600'>
								<SpinningLoadingIcon className='text-cPurple-600' />
								Načítání…
							</div>
						</div>
					</div>
				)}
			</div>

			<div className={`pt-4 flex flex-wrap items-center justify-between gap-4${isFetching ? ' opacity-60' : ''}`}>
				<PaginationPanel pagination={data.pagination} onChange={onPageChange} />
				<PerPageSelect value={perPage} onChange={onPerPageChange} />
			</div>
		</div>
	);
}
